import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import type { SafeString } from '@ember/template/-private/handlebars';
import { buildWaiter } from '@ember/test-waiters';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import config from 'garaje/config/environment';
import preventDefault from 'garaje/helpers/prevent-default';
import { localCopy } from 'tracked-toolbox';

const testWaiter = buildWaiter('employees-profile-photo-uploader-component-waiter');
const isTesting = config.environment === 'test';
const DEFAULT_SIZE = 512;
const DEFAULT_SCALE = 1;

interface EmployeesProfilePhotoUploaderSignature {
  Args: {
    accept?: string;
    /**
     * URL for the user/employee's current photo
     */
    currentPhoto?: string | SafeString;
    /**
     * Size of square output image in px (default: 512)
     */
    outputSize?: number;
    /**
     * How much to zoom in on image (default: 1, min: 1)
     */
    scale?: number | string;
    /**
     * Allowed file types
     */
    validType?: string | RegExp;
    /**
     * Custom validation failure message
     */
    validationError?: string;
    isDisabled?: boolean;
    /**
     * Action for handling errors
     */
    onError?: (error: unknown) => void;
    /**
     * Action triggered by the original file selection
     */
    onFileSelected?: (file: File) => void;
    /**
     * Action triggered after the file is read into a data URL
     */
    onFilePreview?: (dataUrl: string | ArrayBuffer | null) => void;
    /**
     * Action triggered after the image is resized/compressed
     */
    onFilePrepared?: (file: File) => void;
  };
  Element: HTMLDivElement;
}

export default class EmployeesProfilePhotoUploaderInput extends Component<EmployeesProfilePhotoUploaderSignature> {
  // Reference to canvas to render adjusted profile photo
  _canvas?: HTMLCanvasElement;

  // Reference a blank canvas to use as "drag image" (override default "ghost element")
  _dragImage?: HTMLCanvasElement;

  // Reference to async test token
  token: unknown = false;

  // References for Firefox alternative event handling functions
  _handleMousemove?: (event: MouseEvent) => void;
  _handleMouseup?: (event: MouseEvent | DragEvent) => void;

  @tracked isValid = true;
  @tracked isFile = false;
  @tracked fileData?: string | ArrayBuffer | null;
  @tracked previewSrc?: string;
  @tracked isProcessing = false;

  @tracked dragX = 0;
  @tracked dragY = 0;
  @tracked positionX = 0;
  @tracked positionY = 0;

  @localCopy('args.currentPhoto', '') currentPhoto!: EmployeesProfilePhotoUploaderSignature['Args']['currentPhoto'];
  @localCopy('args.outputSize', DEFAULT_SIZE) outputSize!: EmployeesProfilePhotoUploaderSignature['Args']['outputSize'];

  @localCopy('args.validType', /^image\/[^/]+$/)
  validType!: NonNullable<EmployeesProfilePhotoUploaderSignature['Args']['validType']>;
  @localCopy('args.validationError', 'Please select an image file')
  validationError!: EmployeesProfilePhotoUploaderSignature['Args']['validationError'];

  // Action!
  @localCopy('args.onError') onError!: EmployeesProfilePhotoUploaderSignature['Args']['onError'];
  @localCopy('args.onFileSelected') onFileSelected!: EmployeesProfilePhotoUploaderSignature['Args']['onFileSelected'];
  @localCopy('args.onFilePreview') onFilePreview!: EmployeesProfilePhotoUploaderSignature['Args']['onFilePreview'];
  @localCopy('args.onFilePrepared') onFilePrepared!: EmployeesProfilePhotoUploaderSignature['Args']['onFilePrepared'];

  get canvas(): HTMLCanvasElement {
    // eslint-disable-next-line ember/no-side-effects
    return (this._canvas ||= document.createElement('canvas'));
  }

  get validTypeReg(): RegExp {
    const { validType } = this;

    if (validType instanceof RegExp) {
      return validType;
    }

    return new RegExp(validType);
  }

  get safeOutputSize(): number {
    // Size must be at least 1px
    return Math.max(1, Number(this.outputSize) || DEFAULT_SIZE);
  }

  get safeOutputScale(): number {
    // Scale must be at least 1
    return Math.max(1, Number(this.args.scale) || DEFAULT_SCALE);
  }

  get dragImage(): HTMLCanvasElement {
    if (this._dragImage) return this._dragImage;

    const dragImage = document.createElement('canvas');

    dragImage.width = 1;
    dragImage.height = 1;

    // eslint-disable-next-line ember/no-side-effects
    return (this._dragImage = dragImage);
  }

  get handleMousemove(): (event: MouseEvent) => void {
    // eslint-disable-next-line ember/no-side-effects
    return (this._handleMousemove ||= (event) => {
      // If mousemove without button pressed, stop watching
      if (`${event.buttons}` !== '1') {
        return this.removeDocumentMouseEventHandlers();
      }

      if ((<MouseEvent & { mozInputSource: number }>event).mozInputSource) this.repositionPhoto(event);
    });
  }

  get handleMouseup(): (event: MouseEvent | DragEvent) => void {
    // eslint-disable-next-line ember/no-side-effects
    return (this._handleMouseup ||= (_) => {
      this.removeDocumentMouseEventHandlers();
    });
  }

  @action
  fileSelected(files: FileList): void {
    this.isValid = false;
    this.isFile = false;

    const { validationError, onFileSelected, onError } = this;

    if (!files?.length) return;

    const file = files.item(0)!;
    const reader = new FileReader();

    if (!this.validate(file)) {
      onError?.(`${validationError}`);

      return;
    }

    this.token = isTesting ? testWaiter.beginAsync() : false;
    this.isFile = true;

    reader.onload = () => {
      const { result } = reader;

      this.fileData = result;
      this.renderFilePreview(this.fileData);
    };

    reader.readAsDataURL(file);
    onFileSelected?.(file);
  }

  @action
  handleScaleChange(): void {
    const { fileData, isProcessing } = this;

    // If no file is selected or a preview rendering process is already
    // running, don't start a new rendering process.
    if (!fileData || isProcessing) return;

    this.renderFilePreview(fileData);
  }

  @action
  handleDragStart(event: DragEvent): void {
    event.stopPropagation();

    if ((<DragEvent & { mozInputSource: number }>event).mozInputSource) return;

    const { dragImage } = this;

    // Fixes a Chrome quirk
    document.body?.appendChild(dragImage);

    event.dataTransfer!.effectAllowed = 'move';
    event.dataTransfer!.setDragImage?.(dragImage, 0, 0);

    this.dragX = event.x;
    this.dragY = event.y;
  }

  @action
  handleDrag(event: DragEvent): void {
    // Firefox doesn't send valid x, y coordinates with drag events
    if (event.x || event.y) this.repositionPhoto(event);
  }

  @action
  handleDragEnd(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Cleanup
    document.body?.removeChild(this.dragImage);
  }

  /*
    Firefox does not provide pointer coordinates (i.e. event.x and event.y) with `drag` events.

    When we detect Firefox mouse events, fallback to mousedown/mousemove/mouseup
    for adjusting profile photo positioning.

    Why not use mousemove events in all browsers? Mousemove has its own quirks like
    allowing text on the page to be highlighted and dragged.
  */
  @action
  handleMousedown(event: MouseEvent): void {
    // Firefox drag events do not include pointer coordinates
    // Fallback to mousemove events in Firefox (event.mozInputSource === 1).
    if (!(<MouseEvent & { mozInputSource: number }>event).mozInputSource) return;

    (<HTMLCanvasElement>event.target).draggable = false;

    this.dragX = event.x;
    this.dragY = event.y;

    this.addDocumentMouseEventHandlers();
  }

  addDocumentMouseEventHandlers(): void {
    document.addEventListener('mousemove', this.handleMousemove);
    document.addEventListener('mouseup', this.handleMouseup);
  }

  removeDocumentMouseEventHandlers(): void {
    document.removeEventListener('mousemove', this.handleMousemove);
    document.removeEventListener('mouseup', this.handleMouseup);
  }

  repositionPhoto(event: MouseEvent): void {
    const { fileData, isProcessing, dragX, dragY } = this;

    // If no file is selected or a preview rendering process is already
    // running, don't start a new rendering process.
    if (!fileData || isProcessing) return;

    this.positionX = this.positionX + event.x - dragX;
    this.positionY = this.positionY + event.y - dragY;

    this.dragX = event.x;
    this.dragY = event.y;

    this.renderFilePreview(fileData);
  }

  validate(file: File): boolean {
    const typeValid = this.validType ? this.validTypeReg.test(file.type) : true;

    this.isValid = typeValid;

    return this.isValid;
  }

  renderFilePreview(data: string | ArrayBuffer | null): void {
    const imageTag = new Image();

    imageTag.onload = () => {
      this.compressPicture(data, imageTag);
    };

    this.isProcessing = true;
    imageTag.src = <string>data;
    this.onFilePreview?.(data);
  }

  compressPicture(_data: string | ArrayBuffer | null, img: HTMLImageElement): void {
    const {
      safeOutputSize: outputSize,
      safeOutputScale: scale,
      positionX,
      positionY,
      onFilePrepared,
      onError,
      token,
      previewSrc,
    } = this;
    const outputImageAspectRatio = 1; // SQUARE

    if (previewSrc) URL.revokeObjectURL(previewSrc);

    try {
      const { canvas } = this;
      const { naturalWidth, naturalHeight } = img;
      const inputImageAspectRatio = naturalWidth / naturalHeight;

      let dWidth = outputSize * scale;
      let dHeight = outputSize * scale;

      if (inputImageAspectRatio > outputImageAspectRatio) {
        dWidth = naturalWidth * (outputSize / naturalHeight) * scale;
      } else if (inputImageAspectRatio < outputImageAspectRatio) {
        dHeight = naturalHeight * (outputSize / naturalWidth) * scale;
      }

      // Default: centered on X-axis, "rule of 3rds" on Y-axis
      const defaultX = (outputSize - dWidth) * 0.5;
      const defaultY = (outputSize - dHeight) * 0.3;
      const repositionedX = defaultX + positionX;
      const repositionedY = defaultY + positionY;
      const outputX = Math.min(0, Math.max(repositionedX, outputSize - dWidth));
      const outputY = Math.min(0, Math.max(repositionedY, outputSize - dHeight));

      // Keep the photo "in bounds"
      this.positionX = this.positionX + outputX - repositionedX;
      this.positionY = this.positionY + outputY - repositionedY;

      canvas.width = outputSize;
      canvas.height = outputSize;

      const ctx = canvas.getContext('2d');

      ctx?.clearRect(0, 0, outputSize, outputSize);
      ctx?.drawImage(img, outputX, outputY, dWidth, dHeight);

      canvas.toBlob(
        (blob) => {
          const file = new File([blob!], `photo-${Date.now()}.png`, {
            type: blob?.type,
          });

          this.previewSrc = URL.createObjectURL(file);

          onFilePrepared?.(file);

          if (token) testWaiter.endAsync(token);
          this.isProcessing = false;
        },
        'image/png',
        0.8,
      );
    } catch (error) {
      onError?.(error);
      this.isProcessing = false;
    }
  }

  willDestroy(...args: Parameters<Component['willDestroy']>): void {
    super.willDestroy(...args);

    if (this.previewSrc) {
      URL.revokeObjectURL(this.previewSrc);
    }

    this.removeDocumentMouseEventHandlers();

    delete this._handleMousemove;
    delete this._handleMouseup;
    delete this._canvas;
    delete this._dragImage;
    delete this.fileData;
  }

  <template>
    <div
      class="relative"
      data-test-employee-photo-uploader-input
      data-test-employee-photo-uploader-output-size={{this.safeOutputSize}}
      data-test-employee-photo-uploader-output-scale={{this.safeOutputScale}}
      data-test-employee-photo-uploader-is-processing={{this.isProcessing}}
      {{didUpdate this.handleScaleChange this.safeOutputScale}}
      ...attributes
    >
      {{#if this.currentPhoto}}
        <img
          alt=""
          class="object-cover w-full"
          src={{this.currentPhoto}}
          role="presentation"
          data-test-employee-photo-uploader-current-image
        />
      {{else if this.isFile}}
        {{#if this.previewSrc}}
          <img
            alt=""
            class="object-cover w-full h-full"
            src={{this.previewSrc}}
            role="presentation"
            data-private
            data-test-employee-photo-uploader-preview
          />
          {{! template-lint-disable no-pointer-down-event-binding }}
          {{! mousedown event handling for Firefox }}
          <div
            class="absolute top-0 left-0 w-full h-full opacity-0 cursor-move"
            draggable="true"
            {{on "dragstart" this.handleDragStart}}
            {{on "drag" this.handleDrag}}
            {{on "dragend" this.handleDragEnd}}
            {{on "mousedown" this.handleMousedown}}
            data-test-employee-photo-uploader-reposition
            data-test-employee-photo-uploader-position-x={{this.positionX}}
            data-test-employee-photo-uploader-position-y={{this.positionY}}
          >
            Drag to reposition
          </div>
          {{! template-lint-enable no-pointer-down-event-binding }}
        {{/if}}
      {{else}}
        <div class="flex items-center justify-center h-[462px]">
          <label class="block text-center px-16">
            <p class="mx-0 my-2 text-white">Drag a photo here</p>
            <p class="mx-0 my-2 text-carbon-50">or</p>
            <p class="mx-0 my-2 text-white">Select a photo</p>

            <input
              class="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
              type="file"
              accept={{@accept}}
              disabled={{@isDisabled}}
              {{on "change" (pick "target.files" this.fileSelected)}}
              {{on "drop" (preventDefault (pick "dataTransfer.files" this.fileSelected))}}
              data-test-employee-photo-uploader-file-input
            />
          </label>
        </div>
      {{/if}}
    </div>
  </template>
}
